import dayjs from 'dayjs'
import type { RefObject } from 'react'
import {
  ADJACENT_MERGE_THRESHOLD,
  HANDLE_SIZE,
  MAX_SELECTION_MINUTES,
  MINUTES_PER_SLOT,
  MIN_SELECTION_MINUTES,
  SLOT_HEIGHT,
} from '../SchedulingDayBox'

// ============================================================================
// 타입 정의
// ============================================================================

export interface DayBounds {
  dayStart: Date
  dayEnd: Date
}

export interface Point {
  x?: number
  y: number
}

export interface TimeRange {
  start: Date
  end: Date
}

export interface BusyTimeBlock {
  start: Date
  end: Date
}

/**
 * Re-export BusyTimeData from conflictDetection (single source of truth)
 */
export type { BusyTimeData } from './conflictDetection'
import type { BusyTimeData } from './conflictDetection'

export type AttendeeBusyTimes = Record<string, BusyTimeData>

export type InteractionZone = 'top' | 'bottom' | 'middle'

/**
 * 사용자가 선택한 selection block 정보
 */
export interface SelectionTimeRange {
  start: Date
  end: Date
  allday: boolean
}

/**
 * Attendee/Recipient 의 freebusy 정보 가져오기에서 busy block 정보
 * Extends SelectionTimeRange to maintain consistency
 */
export interface BusyInstance extends SelectionTimeRange {
  // Inherits: start, end, allday
}

/**
 * 사용자의 loaded event time block 정보
 * Extends SelectionTimeRange to maintain consistency
 */
export interface MyEvent extends SelectionTimeRange {
  // Inherits: start, end, allday
}

// ============================================================================
// 날짜/시간 관련 유틸리티 함수
// ============================================================================

/**
 * 날짜의 시작 시간(00:00:00)과 종료 시간(23:59:59)을 반환
 *
 * @param date - 기준 날짜
 * @returns 날짜 범위
 */
export const getDayBounds = (date: Date): DayBounds => {
  const dayStart = new Date(date)
  dayStart.setHours(0, 0, 0, 0)
  const dayEnd = new Date(date)
  dayEnd.setHours(23, 59, 59, 999)
  return { dayStart, dayEnd }
}

/**
 * Check if two dates represent the same calendar day (ignoring time)
 *
 * @param date1 - First date
 * @param date2 - Second date
 * @returns true if dates are on the same day, false otherwise
 */
export const isSameDate = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  )
}

/**
 * Check if a date falls within a date range (inclusive, ignoring time)
 *
 * Used for all-day event conflict detection
 *
 * @param targetDate - Date to check
 * @param startDate - Start of range
 * @param endDate - End of range
 * @returns true if targetDate is within the range, false otherwise
 */
export const isDateInRange = (targetDate: Date, startDate: Date, endDate: Date): boolean => {
  // Compare dates at day level (ignoring time)
  const targetDay = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate())
  const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
  const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())

  return targetDay >= startDay && targetDay <= endDay
}

/**
 * Y 좌표를 시간으로 변환
 *
 * @param baseDate - 기준 날짜
 * @param point - Y 좌표가 포함된 포인트 객체
 * @param forEnd - 종료 시간인지 여부 (true면 올림, false면 내림)
 * @returns 변환된 시간
 */
export const calcTime = (baseDate: Date, point: Point, forEnd = false): Date => {
  const inDate = new Date(baseDate)
  inDate.setHours(0, 0, 0, 0)

  const minute = forEnd
    ? Math.ceil(point.y / SLOT_HEIGHT) * MINUTES_PER_SLOT
    : Math.floor(point.y / SLOT_HEIGHT) * MINUTES_PER_SLOT

  inDate.setMinutes(minute)
  return inDate
}

/**
 * Y 좌표를 시간으로 변환 (날짜 경계를 넘어갈 수 있음)
 *
 * 음수 Y 좌표는 이전 날짜, 타임라인 높이를 넘는 Y 좌표는 다음 날짜로 처리
 * 향후 사용을 위해 유지됨
 *
 * @param baseDate - 기준 날짜
 * @param y - Y 좌표
 * @param forEnd - 종료 시간인지 여부 (true면 올림, false면 내림)
 * @returns 변환된 시간 (날짜 경계를 넘을 수 있음)
 */
export const calcTimeFromY = (baseDate: Date, y: number, forEnd = false): Date => {
  const DAY_SLOTS = 48 // 24시간 * 2 (30분 단위)
  const DAY_HEIGHT = DAY_SLOTS * SLOT_HEIGHT

  let targetDate = new Date(baseDate)
  let adjustedY = y

  // 음수 Y 좌표: 이전 날짜
  if (y < 0) {
    const daysBack = Math.ceil(Math.abs(y) / DAY_HEIGHT)
    targetDate.setDate(targetDate.getDate() - daysBack)
    // 나머지 계산: 음수 Y를 양수로 변환
    adjustedY = DAY_HEIGHT + (y % DAY_HEIGHT)
    // 나머지가 음수인 경우 보정
    if (adjustedY < 0) {
      adjustedY += DAY_HEIGHT
    }
  }
  // 타임라인 높이를 넘는 Y 좌표: 다음 날짜
  else if (y >= DAY_HEIGHT) {
    const daysForward = Math.floor(y / DAY_HEIGHT)
    targetDate.setDate(targetDate.getDate() + daysForward)
    adjustedY = y % DAY_HEIGHT
  }

  targetDate.setHours(0, 0, 0, 0)

  const minute = forEnd
    ? Math.ceil(adjustedY / SLOT_HEIGHT) * MINUTES_PER_SLOT
    : Math.floor(adjustedY / SLOT_HEIGHT) * MINUTES_PER_SLOT

  targetDate.setMinutes(minute)
  return targetDate
}

/**
 * 시간을 30분 단위로 스냅
 *
 * @param date - 스냅할 시간
 * @returns 스냅된 시간
 */
export const snapToHalfHour = (date: Date): Date => {
  const snapped = new Date(date)
  const minutes = snapped.getMinutes()
  const remainder = minutes % MINUTES_PER_SLOT

  if (remainder < MINUTES_PER_SLOT / 2) {
    snapped.setMinutes(minutes - remainder, 0, 0)
  } else {
    snapped.setMinutes(minutes + (MINUTES_PER_SLOT - remainder), 0, 0)
  }

  return snapped
}

/**
 * 시간을 날짜와 함께 포맷팅
 *
 * 여러 날에 걸친 selection의 경우, 날짜가 다른 경우 날짜도 함께 표시합니다.
 *
 * @param time - 포맷팅할 시간
 * @param selectionStart - 전체 selection의 시작 시간
 * @param selectionEnd - 전체 selection의 종료 시간
 * @param currentDate - 현재 표시 중인 날짜
 * @param isStart - 시작 시간인지 여부
 * @returns 포맷팅된 시간 문자열
 */
export const formatTimeWithDate = (
  time: Date,
  selectionStart: Date,
  selectionEnd: Date,
  currentDate: Date,
  dateFormat: string,
  timeFormat: string,
  isStart: boolean,
): string => {
  const startDateStr = dayjs(selectionStart).format('YYYY-MM-DD')
  const endDateStr = dayjs(selectionEnd).format('YYYY-MM-DD')
  const timeDateStr = dayjs(time).format('YYYY-MM-DD')
  const currentDateStr = dayjs(currentDate).format('YYYY-MM-DD')

  // 전체 selection이 여러 날에 걸쳐 있는지 확인
  const isMultiDay = startDateStr !== endDateStr

  // 현재 표시 중인 시간이 전체 selection의 시작/종료 경계인지 확인
  const isStartBoundary = isStart && timeDateStr === startDateStr
  const isEndBoundary = !isStart && timeDateStr === endDateStr

  // 여러 날에 걸친 경우, 시작/종료 경계이거나 현재 날짜와 다른 날짜인 경우 날짜 표시
  const shouldShowDate =
    isMultiDay && (isStartBoundary || isEndBoundary || timeDateStr !== currentDateStr)

  const timeStr = dayjs(time).format(timeFormat)

  if (shouldShowDate) {
    // 날짜 포맷 - settings의 dateFormat 사용
    const dateStr = dayjs(time).format(dateFormat)
    return `${dateStr} ${timeStr}`
  }

  // 같은 날짜인 경우 시간만 표시
  return timeStr
}

/**
 * 특정 날짜에 대한 selection 부분 계산
 *
 * 여러 날에 걸친 selection의 경우, 특정 날짜에 표시할 부분을 계산합니다.
 *
 * @param selectionStart - 선택 시작 시간
 * @param selectionEnd - 선택 종료 시간
 * @param targetDate - 대상 날짜
 * @returns 해당 날짜에 표시할 부분 또는 null (겹치지 않는 경우)
 */
export const getSelectionForDate = (
  selectionStart: Date,
  selectionEnd: Date,
  targetDate: Date,
  allDay: boolean,
): TimeRange | null => {
  if (allDay) {
    // Check if targetDate falls within the all-day selection range
    // Use proper date comparison (year, month, day) instead of just day-of-month
    if (isDateInRange(targetDate, selectionStart, selectionEnd)) {
      return { start: targetDate, end: targetDate }
    }
    return null
  }

  const { dayStart, dayEnd } = getDayBounds(targetDate)

  // 선택 범위가 날짜와 겹치지 않는 경우
  // selectionEnd가 dayStart보다 작거나 같으면 겹치지 않음 (같을 때는 다음 날의 시작이므로 제외)
  // selectionStart가 dayEnd보다 크거나 같으면 겹치지 않음 (같을 때는 당일의 끝이므로 포함해야 함)
  if (selectionEnd <= dayStart || selectionStart > dayEnd) {
    return null
  }

  // 겹치는 부분 계산
  const displayStart = new Date(Math.max(selectionStart.getTime(), dayStart.getTime()))
  const displayEnd = new Date(Math.min(selectionEnd.getTime(), dayEnd.getTime()))

  return { start: displayStart, end: displayEnd }
}

// ============================================================================
// 바쁜 시간 관련 유틸리티 함수
// ============================================================================

/**
 * 참석자의 바쁜 시간 블록을 수집
 *
 * 지정된 날짜 범위(dayStart ~ dayEnd)에 해당하는 바쁜 시간 블록만 수집합니다.
 * busy, tentative, outOfOffice 타입의 시간 블록을 모두 포함합니다.
 *
 * @param data - 참석자의 바쁜 시간 데이터
 * @param dayStart - 날짜 시작 시간
 * @param dayEnd - 날짜 종료 시간
 * @returns 지정된 날짜 범위에 해당하는 시간 블록 배열
 */
export const collectBusyBlocks = (
  data: BusyTimeData,
  dayStart: Date,
  dayEnd: Date,
): BusyTimeBlock[] => {
  const blocks: BusyTimeBlock[] = []

  // 바쁜 시간, 임시 예약, 부재 중 시간을 모두 수집
  const timeTypes: Array<keyof BusyTimeData> = ['busy', 'tentative', 'outOfOffice']
  timeTypes.forEach((type) => {
    const typeBlocks = data[type]
    if (typeBlocks && Array.isArray(typeBlocks)) {
      typeBlocks.forEach((block) => {
        // 날짜 범위와 겹치는 블록만 수집
        if (block.start < dayEnd && block.end > dayStart) {
          blocks.push({
            start: new Date(Math.max(block.start.getTime(), dayStart.getTime())),
            end: new Date(Math.min(block.end.getTime(), dayEnd.getTime())),
          })
        }
      })
    }
  })

  return blocks
}

/**
 * 모든 참석자의 바쁜 시간을 연속된 블록으로 병합
 *
 * 지정된 날짜(`date`)에 해당하는 모든 참석자의 바쁜 시간(busy, tentative, outOfOffice)을 수집하고,
 * 시작 시간으로 정렬한 후 겹치거나 인접한 블록을 연속된 시간 범위로 병합합니다.
 *
 * @param attendeeBusyTimes - 이메일을 키로 하는 참석자 바쁜 시간 데이터 맵
 * @param date - 현재 표시 중인 날짜 (이 날짜에 해당하는 busy blocks만 반환)
 * @returns 지정된 날짜에 해당하는 병합된 시간 블록 배열
 */
export const mergeBusyTimes = (
  attendeeBusyTimes: AttendeeBusyTimes,
  date: Date,
): BusyTimeBlock[] => {
  if (!attendeeBusyTimes || Object.keys(attendeeBusyTimes).length === 0) {
    return []
  }

  const { dayStart, dayEnd } = getDayBounds(date)
  const allBlocks: BusyTimeBlock[] = []

  // 모든 참석자의 바쁜 시간 블록 수집 (현재 날짜 범위에 해당하는 것만)
  Object.entries(attendeeBusyTimes).forEach(([_email, data]) => {
    const blocks = collectBusyBlocks(data, dayStart, dayEnd)
    allBlocks.push(...blocks)
  })

  if (allBlocks.length === 0) {
    return []
  }

  // 시작 시간으로 정렬
  allBlocks.sort((a, b) => a.start.getTime() - b.start.getTime())

  // 겹치거나 인접한 블록 병합
  const mergedBlocks: BusyTimeBlock[] = [allBlocks[0]]

  for (let i = 1; i < allBlocks.length; i++) {
    const current = allBlocks[i]
    const last = mergedBlocks[mergedBlocks.length - 1]

    // 겹치거나 인접한 경우(1분 이내) 병합
    if (current.start.getTime() <= last.end.getTime() + ADJACENT_MERGE_THRESHOLD) {
      last.end = new Date(Math.max(last.end.getTime(), current.end.getTime()))
    } else {
      mergedBlocks.push(current)
    }
  }

  return mergedBlocks
}

/**
 * Check if a time range conflicts with busy times and owner events
 *
 * @param startTime - Start time of the range to check
 * @param endTime - End time of the range to check
 * @param busyInstances - Array of attendee busy time instances
 * @param ownerEvents - Array of owner's calendar events
 * @returns true if there is a conflict, false otherwise
 *
 * Conflict detection logic:
 * - Two time ranges overlap if: startTime < otherEndTime AND endTime > otherStartTime
 * - Checks against both attendee busy times and owner's existing events
 */
export const hasConflict = (
  startTime: Date,
  endTime: Date,
  busyInstances: BusyInstance[],
  ownerEvents: MyEvent[],
  isAllDay: boolean,
): boolean => {
  // For all-day selections, convert to full day time range for time-based conflict checking
  let checkStart = startTime
  let checkEnd = endTime

  if (isAllDay) {
    // Convert all-day selection to full day time range (00:00:00 to 23:59:59.999)
    // This ensures proper conflict detection with time-based busy times
    checkStart = new Date(startTime)
    checkStart.setHours(0, 0, 0, 0)

    checkEnd = new Date(endTime)
    checkEnd.setHours(23, 59, 59, 999)
  }

  // Check conflicts with attendee busy times (busy, tentative, out-of-office)
  // Always use time-based comparison for attendee busy times
  const hasBusyConflict = busyInstances.some((busy) => {
    return checkStart < busy.end && checkEnd > busy.start
  })

  if (hasBusyConflict) return true

  // Check conflicts with owner's calendar events
  const hasOwnerConflict = ownerEvents.some((event) => {
    if (isAllDay && event.allday) {
      // Both are all-day events - use date-level comparison
      return (
        isDateInRange(startTime, event.start, event.end) ||
        isDateInRange(endTime, event.start, event.end) ||
        isDateInRange(event.start, startTime, endTime)
      )
    }
    // Time-based comparison for timeline events or when selection is not all-day
    return checkStart < event.end && checkEnd > event.start
  })

  return hasOwnerConflict
}

// ============================================================================
// 시간 선택 검증 유틸리티 함수
// ============================================================================

/**
 * 선택된 시간 범위 유효성 검증
 *
 * - preserveDurationOnBoundary=true일 때: 날짜 경계를 클리핑하지 않고 duration만 검증
 * - preserveDurationOnBoundary=false일 때: 날짜 범위 내로 클리핑하고 duration 검증
 * - 최소/최대 지속 시간 강제
 *
 * @param start - 시작 시간
 * @param end - 종료 시간
 * @param date - 기준 날짜 (날짜 경계 계산용)
 * @param preserveDurationOnBoundary - 날짜 경계를 넘을 때 duration 유지 여부
 * @returns 유효성 검증된 시간 범위
 */
export const validateSelection = (
  start: Date,
  end: Date,
  date: Date,
  preserveDurationOnBoundary = false,
): TimeRange => {
  let validStart = new Date(start)
  let validEnd = new Date(end)

  // 원본 duration 계산
  const originalDurationMinutes = (validEnd.getTime() - validStart.getTime()) / (60 * 1000)

  // 날짜 경계를 넘는 경우 duration 유지 처리
  if (preserveDurationOnBoundary) {
    // 날짜 경계를 클리핑하지 않고 duration만 검증
    // 최소/최대 duration 검증만 수행
    if (originalDurationMinutes < MIN_SELECTION_MINUTES) {
      validEnd = new Date(validStart.getTime() + MIN_SELECTION_MINUTES * 60 * 1000)
    } else if (originalDurationMinutes > MAX_SELECTION_MINUTES) {
      validEnd = new Date(validStart.getTime() + MAX_SELECTION_MINUTES * 60 * 1000)
    }
    // 날짜 경계는 클리핑하지 않음 - 여러 날에 걸칠 수 있음
  } else {
    // 기존 로직: 날짜 범위 내로 클리핑
    const { dayStart, dayEnd } = getDayBounds(date)
    if (validStart < dayStart) validStart = dayStart
    if (validEnd > dayEnd) validEnd = dayEnd

    // 지속 시간 계산 (분)
    const durationMinutes = (validEnd.getTime() - validStart.getTime()) / (60 * 1000)

    // 최소 지속 시간 강제
    if (durationMinutes < MIN_SELECTION_MINUTES) {
      validEnd = new Date(validStart.getTime() + MIN_SELECTION_MINUTES * 60 * 1000)
      if (validEnd > dayEnd) {
        validEnd = dayEnd
        validStart = new Date(validEnd.getTime() - MIN_SELECTION_MINUTES * 60 * 1000)
      }
    }

    // 최대 지속 시간 강제
    if (durationMinutes > MAX_SELECTION_MINUTES) {
      validEnd = new Date(validStart.getTime() + MAX_SELECTION_MINUTES * 60 * 1000)
    }
  }

  return { start: validStart, end: validEnd }
}

// ============================================================================
// 좌표 계산 유틸리티 함수
// ============================================================================

/**
 * 마우스/터치 이벤트의 타임라인 컨테이너 내 상대 좌표 계산
 *
 * @param event - 마우스 이벤트 또는 터치 객체
 * @param containerRef - 타임라인 컨테이너 ref
 * @returns 상대 좌표 또는 null
 */
export const calcPoint = (
  event: MouseEvent | Touch,
  containerRef: RefObject<HTMLElement>,
): Point | null => {
  if (!containerRef?.current) return null

  const bound = containerRef.current.getBoundingClientRect()
  // MouseEvent 또는 Touch 객체 모두 clientX, clientY 속성을 가짐
  const clientX = event.clientX
  const clientY = event.clientY

  return {
    x: clientX - bound.left,
    y: clientY - bound.top,
  }
}

/**
 * 마우스 위치가 선택 영역의 어느 부분인지 판단
 *
 * @param mouseY - 마우스 Y 좌표
 * @param selectionStartY - 선택 영역 시작 Y 좌표
 * @param selectionHeight - 선택 영역 높이
 * @returns 상호작용 영역
 */
export const getInteractionZone = (
  mouseY: number,
  selectionStartY: number,
  selectionHeight: number,
): InteractionZone => {
  const relativeY = mouseY - selectionStartY

  if (relativeY < HANDLE_SIZE) return 'top'
  if (relativeY > selectionHeight - HANDLE_SIZE) return 'bottom'
  return 'middle'
}
